import { addressSchema } from '@/common/schemas/address.schema';
import { optionSchema } from '@/common/schemas/option.schema';
import { intervalToSeconds } from '@/common/utils/intervalToSeconds';
import { unFormatUnit } from '@/common/utils/unFormatters';
import { z } from 'zod';

export const intervalsSchema = z.array(z.object({
  start_time: z.object({
    label: z.string().optional(),
    value: z.string().optional()
  }).optional().nullable(),
  end_time: z.object({
    label: z.string().optional(),
    value: z.string().optional()
  }).optional().nullable(),
}));

export const productGrantedCreditBaseSchema = z.object({
  id: z.string().optional(),
  _destroy: z.boolean().optional(),
  product_id: z.string(),
  product_unit_for_quantity: z.string().optional(),
  display_name: z.string(),
  granted_credit: z.string().optional(),
  granted_quantity: z.string().optional(),
})

export const productCategoryGrantedCreditBaseSchema = z.object({
  id: z.string().optional(),
  _destroy: z.boolean().optional(),
  product_category_id: z.string(),
  display_name: z.string(),
  granted_credit: z.string().optional(),
  granted_quantity: z.string().optional(),
  product_granted_credits_attributes: z.array(productGrantedCreditBaseSchema).optional(),
})

export const productGrantedCreditCurrencySchema = z.object({
  id: z.string().optional(),
  _destroy: z.boolean().optional(),
  product_id: z.string(),
  display_name: z.string(),
  granted_credit: z.string({ message: 'Campo obrigatório' }).min(1, 'Campo obrigatório'),
})

export const productCategoryGrantedCreditCurrencySchema = z.object({
  id: z.string().optional(),
  _destroy: z.boolean().optional(),
  product_category_id: z.string(),
  display_name: z.string(),
  granted_credit: z.string({ message: 'Campo obrigatório' }).min(1, 'Campo obrigatório'),
  product_granted_credits_attributes: z.array(productGrantedCreditCurrencySchema).optional(),
})

export const productGrantedCreditQuantitySchema = z.object({
  id: z.string().optional(),
  _destroy: z.boolean().optional(),
  product_id: z.string(),
  product_unit_for_quantity: z.string().optional(),
  display_name: z.string(),
  granted_quantity: z.string({ message: 'Campo obrigatório' }).min(1, 'Campo obrigatório'),
})

export const productCategoryGrantedCreditQuantitySchema = z.object({
  id: z.string().optional(),
  _destroy: z.boolean().optional(),
  product_category_id: z.string(),
  display_name: z.string(),
  granted_quantity: z.string({ message: 'Campo obrigatório' }).min(1, 'Campo obrigatório'),
  product_granted_credits_attributes: z.array(productGrantedCreditQuantitySchema).optional(),
})

export const productGrantedCreditSchema = productGrantedCreditBaseSchema
export const productCategoryGrantedCreditSchema = productCategoryGrantedCreditBaseSchema

export type ProductGrantedCreditValidationSchema = z.infer<typeof productGrantedCreditSchema>
export type ProductGrantedCreditCurrencyValidationSchema = z.infer<typeof productGrantedCreditCurrencySchema>
export type ProductGrantedCreditQuantityValidationSchema = z.infer<typeof productGrantedCreditQuantitySchema>

export type ProductCategoryGrantedCreditValidationSchema = z.infer<typeof productCategoryGrantedCreditSchema>
export type ProductCategoryGrantedCreditCurrencyValidationSchema = z.infer<typeof productCategoryGrantedCreditCurrencySchema>
export type ProductCategoryGrantedCreditQuantityValidationSchema = z.infer<typeof productCategoryGrantedCreditQuantitySchema>

export const secondsIntervalSchema = z.object({
  days: z.string().optional(),
  hours: z.string().optional(),
  minutes: z.string().optional(),
  seconds: z.string().optional(),
});

export type SecondsIntervalValidationSchema = z.infer<typeof secondsIntervalSchema>;

export const companyBranchSchema = z.object({
  display_name: z.string().min(1, 'Campo Nome é obrigatório'),
  parent: optionSchema.optional().nullable(),
  master_client_user_id: optionSchema,
  general_information_attributes: z.object({
    willUseOwnData: z.boolean().optional(),
    cnpj: z.string().optional().nullable(),
    email: z.string().email('Use um formato de e-mail válido').or(z.literal('')).optional().nullable(),
    fantasy_name: z.string().optional().nullable(),
    official_name: z.string().optional().nullable(),
    primary_cnae: z.string().optional().nullable(),
    state_inscription: z.string().optional().nullable(),
    address_attributes: addressSchema.optional(),
    address: addressSchema.optional()
  }).optional()
    .superRefine((data, ctx) => {
      if (
        data?.willUseOwnData &&
        !data?.cnpj &&
        !data?.email &&
        !data.fantasy_name &&
        !data.official_name &&
        !data.primary_cnae &&
        !data.state_inscription
      ) {
        ctx.addIssue({
          path: ['willUseOwnData'],
          code: z.ZodIssueCode.custom,
          message: 'Caso opte por especificar dados, preencha todos os campos'
        })
      }
      if (data?.willUseOwnData) {
        const missingFields = [];
        if (!data?.address_attributes?.zipcode) missingFields.push("address_attributes.zipcode");
        if (!data?.address_attributes?.street) missingFields.push("address_attributes.street");
        if (!data?.address_attributes?.number) missingFields.push("address_attributes.number");
        if (!data?.address_attributes?.city?.id) missingFields.push("address_attributes.city_id");
        if (!data?.address_attributes?.neighborhood) missingFields.push("address_attributes.neighborhood");
        if (missingFields.length > 0) {
          missingFields.forEach((field) => {
            ctx.addIssue({
              path: field.split("."),
              code: z.ZodIssueCode.custom,
              message: "Este campo é obrigatório",
            });
          });
        }
      }
    }
    ),
  enablePeriodRestriction: z.boolean().optional(),
  period_restrictions_attributes: z.array(z.object({
    id: z.string().optional(),
    week_day: z.string().optional(),
    blocked: z.boolean().optional(),
    intervals_attributes: intervalsSchema.optional(),
  })).optional(),
  granted_credit: z.string({ message: 'Campo obrigatório' }).min(1, 'Campo obrigatório'),
  enableCreditConfiguration: z.boolean().optional(),
  product_category_granted_credits_attributes: z.array(productCategoryGrantedCreditSchema),
  minimum_seconds_interval_between_consumptions: secondsIntervalSchema.optional(),
  minimum_kilometers_usage_between_consumptions: z.string().optional(),
  maximum_kilometers_usage_between_consumptions: z.string().optional(),
  minimum_seconds_usage_between_consumptions: secondsIntervalSchema.optional(),
  maximum_seconds_usage_between_consumptions: secondsIntervalSchema.optional(),
  product_category_restrictions_attributes: z
    .array(
      z.object({
        id: z.string().optional(),
        product_category_id: z.string(),
        display_name: z.string(),
        minimum_seconds_usage_between_consumptions: secondsIntervalSchema.optional(),
        maximum_seconds_usage_between_consumptions: secondsIntervalSchema.optional(),
        minimum_kilometers_usage_between_consumptions: z.string().optional(),
        maximum_kilometers_usage_between_consumptions: z.string().optional(),
      }),
    )
    .superRefine((data, ctx) => {
      data.forEach((restriction, index) => {
        let {
          minimum_seconds_usage_between_consumptions,
          maximum_seconds_usage_between_consumptions,
          maximum_kilometers_usage_between_consumptions,
          minimum_kilometers_usage_between_consumptions,
        } = restriction

        const maximumSecondsIntervalBetweenConsumptions = intervalToSeconds({
          days: Number(maximum_seconds_usage_between_consumptions?.days || 0),
          hours: Number(maximum_seconds_usage_between_consumptions?.hours || 0),
          minutes: Number(maximum_seconds_usage_between_consumptions?.minutes || 0),
          seconds: Number(maximum_seconds_usage_between_consumptions?.seconds || 0),
        })

        const minimumSecondsUsageBetweenConsumption = intervalToSeconds({
          days: Number(minimum_seconds_usage_between_consumptions?.days || 0),
          hours: Number(minimum_seconds_usage_between_consumptions?.hours || 0),
          minutes: Number(minimum_seconds_usage_between_consumptions?.minutes || 0),
          seconds: Number(minimum_seconds_usage_between_consumptions?.seconds || 0),
        })

        const minimumKilometersUsageBetweenConsumptions = minimum_kilometers_usage_between_consumptions
          ? unFormatUnit(minimum_kilometers_usage_between_consumptions, 'Km') || ''
          : ''
        const maximumKilometersUsageBetweenConsumptions = maximum_kilometers_usage_between_consumptions
          ? unFormatUnit(maximum_kilometers_usage_between_consumptions, 'Km') || ''
          : ''

        if (
          !minimumKilometersUsageBetweenConsumptions &&
          !maximumKilometersUsageBetweenConsumptions &&
          !maximumSecondsIntervalBetweenConsumptions &&
          !minimumSecondsUsageBetweenConsumption
        ) {
          ctx.addIssue({
            path: [index],
            code: z.ZodIssueCode.custom,
            message: 'Preencha ao menos um dos campos de restrição de consumo',
          })
        }

        if (maximumSecondsIntervalBetweenConsumptions > 0 && maximumSecondsIntervalBetweenConsumptions < 60) {
          ctx.addIssue({
            path: [index, 'maximum_seconds_usage_between_consumptions'],
            code: z.ZodIssueCode.custom,
            message: 'Deve ser maior ou igual a 1 minuto',
          })
        }

        if (maximumSecondsIntervalBetweenConsumptions >= 2629746) {
          ctx.addIssue({
            path: [index, 'maximum_seconds_usage_between_consumptions'],
            code: z.ZodIssueCode.custom,
            message: 'Deve ser menor ou igual a 30 dias, 10 horas, 29 minutos e 5 segundos',
          })
        }

        if (minimumSecondsUsageBetweenConsumption > 0 && minimumSecondsUsageBetweenConsumption < 60) {
          ctx.addIssue({
            path: [index, 'minimum_seconds_usage_between_consumptions'],
            code: z.ZodIssueCode.custom,
            message: 'Deve ser maior ou igual a 1 minuto',
          })
        }

        if (minimumSecondsUsageBetweenConsumption >= 2629746) {
          ctx.addIssue({
            path: [index, 'minimum_seconds_usage_between_consumptions'],
            code: z.ZodIssueCode.custom,
            message: 'Deve ser menor ou igual a 30 dias, 10 horas, 29 minutos e 5 segundos',
          })
        }
      })
    }),
}).superRefine((data, ctx) => {
  if (
    data?.enablePeriodRestriction &&
    data?.period_restrictions_attributes?.length &&
    data.period_restrictions_attributes.every((periodRestriction) => {
      if (periodRestriction.blocked || periodRestriction.intervals_attributes?.some((interval) => interval.start_time?.value === 'all_day')) {
        return true;
      }
      return periodRestriction.intervals_attributes?.every((interval) => interval.start_time?.value && interval.end_time?.value);
    }) === false
  ) {
    ctx.addIssue({
      path: ['enablePeriodRestriction'],
      code: z.ZodIssueCode.custom,
      message: 'Caso opte por habilitar a restrição de período, preencha todos os campos'
    });
  }

  if (data?.enableCreditConfiguration && !data?.product_category_granted_credits_attributes?.length) {
    ctx.addIssue({
      path: ["enableCreditConfiguration"],
      code: z.ZodIssueCode.custom,
      message: "Caso opte por habilitar o crédito por serviço, selecione pelo menos um serviço",
    });
  }

  const minimumSecondsIntervalBetweenConsumptions = intervalToSeconds({
      days: Number(data?.minimum_seconds_interval_between_consumptions?.days || 0),
      hours: Number(data?.minimum_seconds_interval_between_consumptions?.hours || 0),
      minutes: Number(data?.minimum_seconds_interval_between_consumptions?.minutes || 0),
      seconds: Number(data?.minimum_seconds_interval_between_consumptions?.seconds || 0),
    })

    if (minimumSecondsIntervalBetweenConsumptions > 0 && minimumSecondsIntervalBetweenConsumptions < 60) {
      ctx.addIssue({
        path: ['minimum_seconds_interval_between_consumptions'],
        code: z.ZodIssueCode.custom,
        message: 'Deve ser maior ou igual a 1 minuto',
      })
    }

    if (minimumSecondsIntervalBetweenConsumptions >= 2629746) {
      ctx.addIssue({
        path: ['minimum_seconds_interval_between_consumptions'],
        code: z.ZodIssueCode.custom,
        message: 'Deve ser menor ou igual a 30 dias, 10 horas, 29 minutos e 5 segundos',
      })
    }

    const minimumSecondsUsageBetweenConsumptions = intervalToSeconds({
      days: Number(data?.minimum_seconds_usage_between_consumptions?.days || 0),
      hours: Number(data?.minimum_seconds_usage_between_consumptions?.hours || 0),
      minutes: Number(data?.minimum_seconds_usage_between_consumptions?.minutes || 0),
      seconds: Number(data?.minimum_seconds_usage_between_consumptions?.seconds || 0),
    })

    if (minimumSecondsUsageBetweenConsumptions > 0 && minimumSecondsUsageBetweenConsumptions < 60) {
      ctx.addIssue({
        path: ['minimum_seconds_usage_between_consumptions'],
        code: z.ZodIssueCode.custom,
        message: 'Deve ser maior ou igual a 1 minuto',
      })
    }

    if (minimumSecondsUsageBetweenConsumptions >= 2629746) {
      ctx.addIssue({
        path: ['minimum_seconds_usage_between_consumptions'],
        code: z.ZodIssueCode.custom,
        message: 'Deve ser menor ou igual a 30 dias, 10 horas, 29 minutos e 5 segundos',
      })
    }

    const maximumSecondsUsageBetweenConsumptions = intervalToSeconds({
      days: Number(data?.maximum_seconds_usage_between_consumptions?.days || 0),
      hours: Number(data?.maximum_seconds_usage_between_consumptions?.hours || 0),
      minutes: Number(data?.maximum_seconds_usage_between_consumptions?.minutes || 0),
      seconds: Number(data?.maximum_seconds_usage_between_consumptions?.seconds || 0),
    })

    if (maximumSecondsUsageBetweenConsumptions > 0 && maximumSecondsUsageBetweenConsumptions < 60) {
      ctx.addIssue({
        path: ['maximum_seconds_usage_between_consumptions'],
        code: z.ZodIssueCode.custom,
        message: 'Deve ser maior ou igual a 1 minuto',
      })
    }

    if (maximumSecondsUsageBetweenConsumptions >= 2629746) {
      ctx.addIssue({
        path: ['maximum_seconds_usage_between_consumptions'],
        code: z.ZodIssueCode.custom,
        message: 'Deve ser menor ou igual a 30 dias, 10 horas, 29 minutos e 5 segundos',
      })
    }
});

export const companyBranchCurrencySchema = companyBranchSchema

export const companyBranchQuantitySchema = companyBranchSchema.innerType().extend({
  product_category_granted_credits_attributes: z.array(productCategoryGrantedCreditQuantitySchema),
})

export type CompanyBranchValidationSchema = z.infer<typeof companyBranchSchema>
export type CompanyBranchCurrencyValidationSchema = z.infer<typeof companyBranchCurrencySchema>
export type CompanyBranchQuantityValidationSchema = z.infer<typeof companyBranchQuantitySchema>
